'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { History, Upload } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { PageHeader } from '@/components/page-header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Skeleton } from '@/components/ui/skeleton';
import { ActivityDataImportEntryPoint } from '@/features/ghg/components';
import { ActivityDataImportHistory } from '@/features/ghg/components/ActivityDataImportHistory';
import { useGHGPagePeriod } from '@/features/ghg/hooks/use-ghg-page-period';
import type { ActivityDataResponse } from '@/features/ghg/types/activity-data';
import { useOrganizationId } from '@/hooks/use-organization-id';
import i18n from '@/lib/i18n';

const collectSearchSchema = z.object({
  taskId: z.string().optional(),
  uploadId: z.string().optional(),
});

export const Route = createFileRoute('/_authenticated/ghg/collect')({
  validateSearch: collectSearchSchema,
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.collectData'),
  }),
  component: CollectDataPage,
});

function CollectDataPage() {
  const { t } = useTranslation('ghg');
  const navigate = useNavigate();
  const { taskId: searchTaskId, uploadId } = Route.useSearch();
  const { periodId } = useGHGPagePeriod();
  const orgId = useOrganizationId();

  const [preferredIntakePath, setPreferredIntakePath] = useState<'manual' | 'file' | null>(
    uploadId ? 'file' : searchTaskId ? 'manual' : null
  );

  useEffect(() => {
    if (uploadId) {
      setPreferredIntakePath('file');
      return;
    }
    if (searchTaskId) {
      setPreferredIntakePath('manual');
    }
  }, [searchTaskId, uploadId]);

  const handleFormSuccess = (entry: ActivityDataResponse) => {
    if (searchTaskId) {
      toast.success(t('toast.entryCreatedLinkedToTask'), {
        description: t('collectPage.toast_taskContribution'),
      });
      void navigate({ to: '/tasks/$taskId', params: { taskId: searchTaskId } });
    } else {
      toast.success(t('toast.entryCreated'), {
        description: t('collectPage.toast_entryCreatedSuccess'),
        action: {
          label: t('collectPage.viewEmissions'),
          onClick: () => void navigate({ to: '/ghg/emissions' }),
        },
      });
      void navigate({
        to: '/ghg/activity-data/$entryId',
        params: { entryId: entry.id },
      });
    }
  };

  const handleImportComplete = () => {
    setPreferredIntakePath(null);
    void navigate({ to: '/datahub/my-entries' });
  };

  if (!orgId) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-7xl">
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <PageHeader
          title={t('collectPage.title')}
          description={t('collectPage.description')}
          module="ghg"
          icon={<Upload className="h-6 w-6" />}
        />

        <ActivityDataImportEntryPoint
          key={`collect-intake-${periodId}-${searchTaskId ?? 'none'}`}
          defaultReportingPeriodId={periodId}
          taskId={searchTaskId}
          manualPresentation="inline"
          initialPath={preferredIntakePath}
          onResetToSelector={() => {
            setPreferredIntakePath(null);
            if (!searchTaskId && !uploadId) return;
            void navigate({ to: '/ghg/collect', replace: true });
          }}
          onManualSuccess={handleFormSuccess}
          onFileImportComplete={handleImportComplete}
          onExit={() => {
            void navigate({ to: '/ghg/activity-data' });
          }}
          fileSupplementaryContent={
            <Collapsible>
              <CollapsibleTrigger className="flex w-full items-center gap-2 rounded-lg border px-4 py-3 text-sm font-medium hover:bg-muted/50">
                <History className="h-4 w-4 text-muted-foreground" />
                {t('collectPage.importHistory')}
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-3">
                <Card>
                  <CardHeader>
                    <CardTitle>{t('collectPage.importHistory')}</CardTitle>
                    <CardDescription>
                      {t('collectPage.importHistoryDescription')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ActivityDataImportHistory />
                  </CardContent>
                </Card>
              </CollapsibleContent>
            </Collapsible>
          }
        />
      </div>
    </div>
  );
}
